import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import "./Home.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faHome,
  faBook,
  faChalkboardTeacher,
  faLightbulb,
} from "@fortawesome/free-solid-svg-icons";

// Import komponen halaman
import Cermati from "../pages/Cermati";
import Eksplorasi from "../pages/Eksplorasi";
import RancangDesain from "../pages/RancangDesain";
import Asah from "../pages/Asah";
import Sebar from "../pages/Sebar";
import Rancang from "../pages/MenulisPage";
import NotificationBell from "./NotificationBell";

// Import komponen baru
import PanduanContent from "./PanduanContent";
import TutorialContent from "./TutorialContent";
import BahanBelajarContent from "./BahanBelajarContent";

// Import data
import { features } from "./data/featuresData";
import { tutorialVideos } from "./data/tutorialVideosData";
import { clearAuthSession, getValidToken } from "../services/api";
import { toast } from "react-toastify";
import { VALID_TABS, LEARNING_STAGES, tabFromSearchParams } from "../constants/navigation";
import { useModalA11y } from "../hooks/useModalA11y";

const SIDEBAR_ITEMS = [
  { tab: "beranda", icon: faHome, label: "BERANDA" },
  { tab: "panduan", icon: faBook, label: "PANDUAN" },
  { tab: "tutorial", icon: faChalkboardTeacher, label: "TUTORIAL" },
  { tab: "bahan", icon: faLightbulb, label: "BAHAN BELAJAR" },
];

const SEARCH_TARGETS = [
  { keys: ["beranda", "home"], tab: "beranda", label: "Beranda" },
  { keys: ["cermati", "capaian", "materi"], tab: "cermati", label: "Cermati" },
  { keys: ["eksplorasi", "contoh"], tab: "eksplorasi", label: "Eksplorasi" },
  { keys: ["rancang", "menulis", "naskah"], tab: "rancang", label: "Rancang" },
  { keys: ["desain", "storyboard"], tab: "desain", label: "Desain" },
  { keys: ["asah", "refleksi", "rubrik"], tab: "asah", label: "Asah" },
  { keys: ["sebar", "galeri", "karya"], tab: "sebar", label: "Sebar" },
  { keys: ["panduan", "manual", "petunjuk"], tab: "panduan", label: "Panduan" },
  { keys: ["tutorial"], tab: "tutorial", label: "Tutorial" },
  { keys: ["bahan", "belajar"], tab: "bahan", label: "Bahan Belajar" },
];

function findSearchTarget(query) {
  const q = query.trim().toLowerCase();
  if (!q) return null;
  return SEARCH_TARGETS.find(
    ({ keys, label }) =>
      label.toLowerCase().includes(q) || keys.some((k) => k.includes(q) || q.includes(k))
  );
}

const Home = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeContent, setActiveContent] = useState(() => tabFromSearchParams(searchParams));
  const [currentSlide, setCurrentSlide] = useState(0);
  const [user, setUser] = useState(null);
  const [showQuizModal, setShowQuizModal] = useState(false);
  const [showProfilModal, setShowProfilModal] = useState(false);
  const [showAgendaModal, setShowAgendaModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const navigate = useNavigate();

  const navigateToTab = useCallback(
    (tab) => {
      if (!VALID_TABS.has(tab)) return;
      setActiveContent(tab);
      if (tab === "beranda") {
        setSearchParams({}, { replace: true });
      } else {
        setSearchParams({ tab }, { replace: true });
      }
    },
    [setSearchParams]
  );

  useEffect(() => {
    const tab = tabFromSearchParams(searchParams);
    setActiveContent(tab);
  }, [searchParams]);

  const currentStage = LEARNING_STAGES.find((s) => s.tab === activeContent);
  const userProfileId = user?.id || user?._id;

  // ✅ Cek token login
  useEffect(() => {
    const token = getValidToken();
    const userData = localStorage.getItem("user");

    if (!token || !userData) {
      clearAuthSession();
      navigate("/login");
    } else {
      setUser(JSON.parse(userData));
    }
  }, [navigate]);

  const handleLogout = async () => {
    const refreshToken = localStorage.getItem('refreshToken');
    try {
      await fetch('/api/auth/logout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      });
    } catch {
      /* ignore */
    }
    clearAuthSession();
    navigate("/login");
  };

  const totalSlides = Math.ceil(features.length / 2);

  // ✅ Ganti slide otomatis
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % totalSlides);
    }, 5000);
    return () => clearInterval(interval);
  }, [totalSlides]);

  const handleSearch = (e) => {
    e.preventDefault();
    const match = findSearchTarget(searchQuery);
    if (match) {
      navigateToTab(match.tab);
      toast.success(`Membuka ${match.label}`);
      setSearchQuery("");
    } else {
      toast.info("Tidak ditemukan. Coba: Cermati, Desain, Sebar, Panduan…");
    }
  };

  const closeQuizModal = useCallback(() => setShowQuizModal(false), []);
  const closeProfilModal = useCallback(() => setShowProfilModal(false), []);
  const closeAgendaModal = useCallback(() => setShowAgendaModal(false), []);
  const quizModalRef = useModalA11y(showQuizModal, closeQuizModal);
  const profilModalRef = useModalA11y(showProfilModal, closeProfilModal);
  const agendaModalRef = useModalA11y(showAgendaModal, closeAgendaModal);

  return (
    <div className="home-container">
      {/* Sidebar */}
      <aside className="sidebar">
        <div className="sidebar-header">
          <img
            src="/logo/logo-upi.png"
            alt="Logo Rumah Cerdas"
            className="logo-sidebar"
          />
          <h2>Menu Belajar</h2>
        </div>
        <ul>
          {SIDEBAR_ITEMS.map(({ tab, icon, label }) => (
            <li key={tab}>
              <button
                type="button"
                className={`sidebar-nav-btn ${activeContent === tab ? "active" : ""}`}
                onClick={() => navigateToTab(tab)}
                aria-current={activeContent === tab ? "page" : undefined}
              >
                <span className="menu-icon" aria-hidden="true">
                  <FontAwesomeIcon icon={icon} />
                </span>
                <span>{label}</span>
              </button>
            </li>
          ))}
        </ul>
      </aside>

      {/* Main Layout */}
      <main className="main-content">
        {/* Header tetap */}
        <header className="top-header">
          <div className="logo-section">
            <img src="/logo/kemdikbud.png" alt="Logo Kemdikbud" className="logo" />
            <img src="/logo/bpi.jpeg" alt="Logo BPI" className="logo" />
            <img src="/logo/lpdp.png" alt="Logo LPDP" className="logo" />
            <h1>RUMAH BELAJAR CERDAS</h1>
          </div>

          {/* ✅ Ganti tombol login dengan profil user */}
          <div className="search-section">
            <form className="search-box" onSubmit={handleSearch}>
              <input
                type="search"
                placeholder="Cari ruang belajar…"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                aria-label="Cari ruang belajar"
              />
              <button type="submit" className="btn-search">CARI</button>
            </form>
            {user ? (
              <div className="user-info">
                <NotificationBell onNavigate={navigateToTab} />
                {userProfileId ? (
                  <Link to={`/profile/${userProfileId}`} className="user-name-link">
                    👋 {user.nama || "Pengguna"} ({user.role})
                  </Link>
                ) : (
                  <span className="user-name">
                    👋 {user.nama || "Pengguna"} ({user.role})
                  </span>
                )}
                <button className="btn-logout" onClick={handleLogout}>
                  Logout
                </button>
              </div>
            ) : (
              <button className="btn-login" onClick={() => navigate("/login")}>
                MASUK
              </button>
            )}
          </div>
        </header>

        {/* Menu utama */}
        <nav className="menu-bar">
          <button className="menu-btn stage-1" onClick={() => navigateToTab("cermati")}>
            <div className="btn-icon" aria-hidden="true">👀</div>
            <span>CERMATI</span>
          </button>
          <button className="menu-btn stage-2" onClick={() => navigateToTab("eksplorasi")}>
            <div className="btn-icon" aria-hidden="true">🔍</div>
            <span>EKSPLORASI</span>
          </button>
          <button className="menu-btn stage-3" onClick={() => navigateToTab("rancang")}>
            <div className="btn-icon" aria-hidden="true">🧠</div>
            <span>RANCANG</span>
          </button>
          <button className="menu-btn stage-4" onClick={() => navigateToTab("desain")}>
            <div className="btn-icon" aria-hidden="true">🎨</div>
            <span>DESAIN</span>
          </button>
          <button className="menu-btn stage-5" onClick={() => navigateToTab("asah")}>
            <div className="btn-icon" aria-hidden="true">🎯</div>
            <span>ASAH</span>
          </button>
          <button className="menu-btn stage-6" onClick={() => navigateToTab("sebar")}>
            <div className="btn-icon" aria-hidden="true">🌍</div>
            <span>SEBAR</span>
          </button>
        </nav>

        {currentStage && (
          <div className="stage-indicator" aria-live="polite">
            Tahap {currentStage.step}/6 — {currentStage.label}
          </div>
        )}

        {/* ===== Konten Dinamis ===== */}
        <section className="dynamic-content">
          {activeContent === "beranda" ? (
            <>
              {/* === Slider === */}
              <div className="slider-container">
                <div
                  className="slider-track"
                  style={{
                    transform: `translateX(-${currentSlide * 100}%)`,
                  }}
                >
                  {features.map((f, i) => (
                    <div key={i} className={`feature-card ${f.className}`}>
                      <img src={f.icon} alt={f.title} className="card-banner" />
                    </div>
                  ))}
                </div>

                <div className="slider-dots">
                  {Array.from({ length: totalSlides }).map((_, idx) => (
                    <button
                      key={idx}
                      type="button"
                      className={`dot ${currentSlide === idx ? "active" : ""}`}
                      onClick={() => setCurrentSlide(idx)}
                      aria-label={`Slide ${idx + 1} dari ${totalSlides}`}
                      aria-current={currentSlide === idx ? "true" : undefined}
                    />
                  ))}
                </div>
              </div>

              {/* Info Section */}
              <section className="info-section">
                <div
                  className="info-card info-card-clickable"
                  onClick={() => setShowProfilModal(true)}
                  onKeyDown={(e) => e.key === "Enter" && setShowProfilModal(true)}
                  role="button"
                  tabIndex={0}
                >
                  <img src="/logo/profil2.png" alt="Profil Pengembang" />
                  <p>Profil Pengembang</p>
                </div>
                <div className="info-card info-card-clickable" onClick={() => setShowQuizModal(true)} role="button" tabIndex={0}>
                  <img src="/logo/rate.png" alt="Quiz" />
                  <p>Quiz</p>
                </div>
                <div
                  className="info-card info-card-clickable"
                  onClick={() => setShowAgendaModal(true)}
                  onKeyDown={(e) => e.key === "Enter" && setShowAgendaModal(true)}
                  role="button"
                  tabIndex={0}
                >
                  <img src="/logo/calendar.png" alt="Agenda" />
                  <p>Agenda</p>
                </div>
                <div className="contact-right">
                  <img
                    src="/foto/handayani.jpg"
                    alt="Hanihandayani"
                    className="profile-photo"
                  />
                  <p>
                    Hubungi di Surel<br />
                    <a href="mailto:hanihandayani748@gmail.com">
                      hanihandayani748@gmail.com
                    </a>
                  </p>
                </div>
              </section>

              {/* Footer */}
              <footer className="footer">
                <div className="footer-content">
                  <p className="tagline">
                    Jelajahi Ruang Belajar Di Rumah CERDAS<br />
                    Cerita Edukatif Reflektif Dalam Aplikasi Smart
                  </p>
                </div>
              </footer>
            </>
          ) : (
            <>
              {activeContent === "cermati" && <Cermati />}
              {activeContent === "eksplorasi" && <Eksplorasi />}
              {activeContent === "rancang" && (
                <Rancang onGoToDesain={() => navigateToTab("desain")} />
              )}
              {activeContent === "desain" && <RancangDesain />}
              {activeContent === "asah" && <Asah />}
              {activeContent === "sebar" && <Sebar />}
              {activeContent === "panduan" && (
                <PanduanContent onOpenRoom={(tab) => navigateToTab(tab)} />
              )}
              {activeContent === "tutorial" && <TutorialContent tutorialVideos={tutorialVideos} />}
              {activeContent === "bahan" && (
                <BahanBelajarContent onOpenCermati={() => navigateToTab("cermati")} />
              )}
            </>
          )}
        </section>
      </main>

      {/* Quiz Modal */}
      {showQuizModal && (
        <div className="modal-overlay" onClick={closeQuizModal} role="presentation">
          <div
            ref={quizModalRef}
            className="modal-content"
            role="dialog"
            aria-modal="true"
            aria-labelledby="quiz-modal-title"
            onClick={(e) => e.stopPropagation()}
          >
            <button type="button" className="modal-close" onClick={closeQuizModal} aria-label="Tutup quiz">
              ×
            </button>
            <h2 id="quiz-modal-title" className="sr-only">Quiz Narasi Digital Storytelling</h2>
            <iframe
              src="https://www.educaplay.com/learning-resources/26567507-narasi_digital_storytelling.html"
              width="100%"
              height="600"
              frameBorder="0"
              title="Quiz Narasi Digital Storytelling"
            ></iframe>
          </div>
        </div>
      )}

      {showProfilModal && (
        <div className="modal-overlay" onClick={closeProfilModal} role="presentation">
          <div
            ref={profilModalRef}
            className="modal-content modal-content-sm"
            role="dialog"
            aria-modal="true"
            aria-labelledby="profil-modal-title"
            onClick={(e) => e.stopPropagation()}
          >
            <button type="button" className="modal-close" onClick={closeProfilModal} aria-label="Tutup">
              ×
            </button>
            <h2 id="profil-modal-title" className="info-modal-title">Profil Pengembang</h2>
            <div className="info-modal-body">
              <img src="/foto/handayani.jpg" alt="Hanihandayani" className="info-modal-photo" />
              <p><strong>Hanihandayani, S.Pd.</strong></p>
              <p>Pengembang Website Rumah Belajar CERDAS — Cerita Edukatif Reflektif Dalam Aplikasi Smart.</p>
              <p>
                Surel:{" "}
                <a href="mailto:hanihandayani748@gmail.com">hanihandayani748@gmail.com</a>
              </p>
            </div>
          </div>
        </div>
      )}

      {showAgendaModal && (
        <div className="modal-overlay" onClick={closeAgendaModal} role="presentation">
          <div
            ref={agendaModalRef}
            className="modal-content modal-content-sm"
            role="dialog"
            aria-modal="true"
            aria-labelledby="agenda-modal-title"
            onClick={(e) => e.stopPropagation()}
          >
            <button type="button" className="modal-close" onClick={closeAgendaModal} aria-label="Tutup">
              ×
            </button>
            <h2 id="agenda-modal-title" className="info-modal-title">Agenda Belajar</h2>
            <div className="info-modal-body">
              <ul className="agenda-list">
                <li>📅 Workshop Digital Storytelling — hubungi pengembang untuk jadwal terbaru</li>
                <li>🎬 Sesi praktik storyboard & export video di Ruang Desain</li>
                <li>🌍 Pameran karya di Ruang Sebar setiap akhir unit pembelajaran</li>
              </ul>
              <p className="agenda-note">Agenda detail akan diumumkan melalui guru mata pelajaran.</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Home;